import { TShirt } from "@phosphor-icons/react"
import { mapBy, uniq } from "~/utils/tsUtils"
import { useMemo } from "react"
import Select from "~/components/reusable/Select"
import {
  useGetCollections,
  useCollectionGroups,
  type ICollectionSimple,
} from "~/hooks/useCollections"
import { startOfTodayUTC } from "~/utils/datetime"
import { type ICollectionType } from "~/models/collection"
import { ActionMeta } from "react-select"
import { LinkedBadge } from "~/components/collections/LinkedBadge"

interface CollectionsSelectProps {
  selectedCollectionIds: number[]
  onCollectionSelectChange: (collections: ICollectionSimple[]) => void
  excludedCollectionIds?: number[]
  excludeNoos?: boolean
  label?: string
  isFilter?: boolean
  className?: string
  isMulti?: boolean
  excludeExpired?: boolean
  excludeCollectionTypes?: ICollectionType[]
  placeholder?: string
  autoFocus?: boolean
  disabled?: boolean
  selectedGroupIds?: number[]
  excludeGroupsOnlyWhenFull?: boolean
  linkedCollectionIds?: number[]
  /** Grouped collections are listed last and disabled: only their whole group can be picked */
  groupsOnly?: boolean
  /**
   * Disables an option when this returns a reason, shown as its tooltip. A group option is passed
   * all its members; the groupsOnly reason wins over this one
   */
  getDisabledReason?: (collections: ICollectionSimple[]) => string | undefined
}

interface SelectedOption {
  label: string
  value: ICollectionSimple[]
  isGroup?: boolean
  isSelected?: boolean
  isDisabled?: boolean
  tooltip?: string
}

const today = startOfTodayUTC()

const withDisabledReason = (option: SelectedOption, reason: string | undefined): SelectedOption =>
  reason ? { ...option, isDisabled: true, tooltip: reason } : option

const disabledLast = (a: SelectedOption, b: SelectedOption) =>
  Number(!!a.isDisabled) - Number(!!b.isDisabled)

export default function CollectionsSelect({
  selectedCollectionIds,
  onCollectionSelectChange,
  label,
  isFilter,
  className,
  isMulti = false,
  excludedCollectionIds,
  excludeExpired = false,
  excludeCollectionTypes = [],
  placeholder = "Select collection...",
  autoFocus = false,
  excludeNoos = false,
  disabled = false,
  selectedGroupIds,
  excludeGroupsOnlyWhenFull = false,
  linkedCollectionIds,
  groupsOnly = false,
  getDisabledReason,
}: CollectionsSelectProps) {
  const { data: collections } = useGetCollections({ ignoreNoos: excludeNoos })
  const { data: groups } = useCollectionGroups()

  const excludedGroupIds = useMemo(() => {
    if (!excludedCollectionIds || excludedCollectionIds.length === 0 || !groups) return []
    if (excludeGroupsOnlyWhenFull) {
      const excludedSet = new Set(excludedCollectionIds)
      return groups
        .filter((g) => g.collectionIds.every((id) => excludedSet.has(id)))
        .map((g) => g.id)
    }
    return uniq(
      excludedCollectionIds.map(
        (colId) => collections?.find((c) => c.id === colId)?.collectionGroupId,
      ) || [],
    )
  }, [excludedCollectionIds, collections, groups, excludeGroupsOnlyWhenFull])

  const filteredCollections = useMemo(() => {
    if (!collections) return []

    const activeGroupIds = excludeExpired
      ? new Set(
          collections
            .filter((c) => c.collectionGroupId && (!c.endDate || new Date(c.endDate) >= today))
            .map((c) => c.collectionGroupId),
        )
      : null

    return collections.filter((c) => {
      if (excludeExpired && c.endDate && new Date(c.endDate) < today) {
        if (!c.collectionGroupId || !activeGroupIds?.has(c.collectionGroupId)) return false
      }
      return !excludeCollectionTypes.includes(c.collectionType as ICollectionType)
    })
  }, [collections, excludeExpired, excludeCollectionTypes])

  const filteredGroups = useMemo(() => {
    if (!groups) return []
    return groups
      .filter((g) => !excludedGroupIds.includes(g.id))
      .filter((g) => filteredCollections.some((c) => c.collectionGroupId === g.id))
  }, [groups, excludedGroupIds, filteredCollections])

  const dropdownOptions: { label: string; options: SelectedOption[] }[] = useMemo(() => {
    if (!groups) return []

    const groupOptions = filteredGroups
      .filter((g) => !excludedGroupIds.includes(g.id))
      .map((g) => {
        const value = collections?.filter((c) => c.collectionGroupId === g.id) ?? []
        const option: SelectedOption = {
          label: g.name,
          value,
          isGroup: true,
          // If all collections in the group are selected, mark the group as selected
          isSelected: filteredCollections
            ?.filter((c) => c.collectionGroupId === g.id)
            .every((c) => selectedCollectionIds.includes(c.id)),
        }
        return withDisabledReason(option, getDisabledReason?.(value))
      })
      .filter((g) => g.value.length > 0)
      .sort(disabledLast)

    const groupNameById = new Map(groups.map((g) => [g.id, g.name]))
    const individualOptions: SelectedOption[] = filteredCollections
      .filter((c) => !excludedCollectionIds?.includes(c.id))
      .map((c) => {
        const groupName =
          groupsOnly && c.collectionGroupId && groupNameById.get(c.collectionGroupId)
        const reason = groupName
          ? `Part of ${groupName}. Choose ${groupName} to compare with it.`
          : getDisabledReason?.([c])
        const option: SelectedOption = {
          label: c.name,
          value: [c],
          isGroup: false,
          isSelected: selectedCollectionIds.includes(c.id),
        }
        return withDisabledReason(option, reason)
      })
      .sort(disabledLast)

    return [
      { label: "Grouped", options: groupOptions },
      { label: "Individual", options: individualOptions },
    ]
  }, [
    filteredCollections,
    collections,
    groups,
    excludedGroupIds,
    excludedCollectionIds,
    selectedCollectionIds,
    filteredGroups,
    groupsOnly,
    getDisabledReason,
  ])

  const selected = useMemo(() => {
    if (!groups || !filteredCollections) return null

    const selectedItems = filteredCollections.filter((c) => selectedCollectionIds.includes(c.id))
    if (selectedItems.length === 0) return null

    const groupSelected: SelectedOption[] = []
    const singleSelected: SelectedOption[] = []
    const selectedSet = new Set(selectedCollectionIds)

    for (const group of groups) {
      const groupItems = filteredCollections.filter((c) => c.collectionGroupId === group.id)
      const shouldShowAsGroup = selectedGroupIds
        ? selectedGroupIds.includes(group.id)
        : groupItems.length > 0 && groupItems.every((c) => selectedSet.has(c.id))
      if (shouldShowAsGroup) {
        const selectedGroupItems = groupItems.filter((c) => selectedSet.has(c.id))
        if (selectedGroupItems.length > 0) {
          groupSelected.push({
            label: group.name,
            value: selectedGroupItems,
            isGroup: true,
            isSelected: true,
          })
          selectedGroupItems.forEach((c) => selectedSet.delete(c.id))
        }
      }
    }

    for (const id of selectedSet) {
      const c = filteredCollections.find((x) => x.id === id)
      if (c) singleSelected.push({ label: c.name, value: [c], isGroup: false, isSelected: true })
    }

    const all = [...groupSelected, ...singleSelected]
    return !isMulti ? (all[0] ?? null) : all
  }, [selectedCollectionIds, filteredCollections, groups, isMulti, selectedGroupIds])

  const linkedIds = new Set(linkedCollectionIds)
  const formatOptionLabel = (option: SelectedOption) => (
    <span className="flex items-center gap-1.5">
      {option.label}
      {!option.isGroup && option.value.some((c) => linkedIds.has(c.id)) && <LinkedBadge />}
    </span>
  )

  const handleChange = (
    newValue: SelectedOption | readonly SelectedOption[] | null,
    actionMeta: ActionMeta<SelectedOption>,
  ) => {
    if (isMulti && selected && actionMeta.action === "deselect-option") {
      // Handle deselect option
      const deselected = actionMeta.option
      const deselectedIds = new Set(deselected?.value.map((c) => c.id))
      // If a single value (from a group) was removed, and a group is selected, only keep the remaining items from that group
      if (deselectedIds.size > 0) {
        let remaining: ICollectionSimple[] = []
        if (Array.isArray(selected)) {
          remaining = selected.flatMap((sel) => sel.value).filter((c) => !deselectedIds.has(c.id))
        } else {
          remaining = selected.value.filter((c) => !deselectedIds.has(c.id))
        }
        onCollectionSelectChange(remaining)
        return
      }
    }

    const selectedList = (Array.isArray(newValue) ? newValue : newValue ? [newValue] : []).flatMap(
      (o) => o.value,
    )
    onCollectionSelectChange([...mapBy(selectedList, "id").values()])
  }

  return (
    <Select
      label={label}
      className={className}
      size={isFilter ? undefined : "sm"}
      isClearable
      isMulti={isMulti}
      hideSelectedOptions={false}
      placeholder={isFilter ? "Select..." : placeholder}
      value={selected}
      closeMenuOnSelect={!isMulti}
      // This is nessecary to make options with same collection names but different ids selectable
      getOptionValue={(option) =>
        `${option.label}-${option.isGroup ? "group" : "single"}-${option.value
          .map((c) => c.id)
          .join("-")}`
      }
      onChange={handleChange}
      options={dropdownOptions}
      // This is necessary to mark individual options as selected when their ids are in selectedCollectionIds
      isOptionSelected={(option) => option.isSelected ?? false}
      leftIcon={<TShirt />}
      autoFocus={autoFocus}
      openMenuOnFocus={autoFocus}
      isDisabled={disabled}
      formatOptionLabel={linkedCollectionIds?.length ? formatOptionLabel : undefined}
    />
  )
}
